// Pieces shared by the Sales Manager Intelligence pages (trustedge sales_agent/).
import { useContext } from "react";
import { Clock } from "lucide-react";
import dayjs from "dayjs";
import { AuthContext } from "../../context/AuthContext";
import { cn } from "../../utils/tailwindUtils";
import { formatINR } from "../../components/dashboard/shared";

export const PRIORITY_STYLE = {
    critical: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
    high: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    medium: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300",
    low: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
};

export const STATUS_LABEL = {
    new: "New", acknowledged: "Acknowledged", in_progress: "In progress",
    resolved: "Resolved", dismissed: "Dismissed", reopened: "Reopened",
};

export function PriorityChip({ priority, score }) {
    return (
        <span className={cn("inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase",
            PRIORITY_STYLE[priority] || PRIORITY_STYLE.low)}>
            {priority}{score != null && <span className="font-normal tabular-nums opacity-80">{score}</span>}
        </span>
    );
}

// One KPI value as text, by its unit. null is "not available", never 0.
export function formatMetric(metric, unit) {
    const v = metric?.value;
    if (v === null || v === undefined) return "—";
    if (unit === "inr") return formatINR(v);
    if (unit === "pct") return `${v}%`;
    if (unit === "hours") return `${v} h`;
    if (unit === "days") return `${v} d`;
    return Number(v).toLocaleString("en-IN");
}

export function Freshness({ calculatedAt, rulesAt, extra }) {
    return (
        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-400">
            <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />
                Figures as of {calculatedAt ? dayjs(calculatedAt).format("DD MMM, hh:mm A") : "—"} IST</span>
            <span>Alert rules last ran {rulesAt ? dayjs(rulesAt).format("DD MMM, hh:mm A") : "not yet"}</span>
            {extra}
        </p>
    );
}

export function ScopePicker({ scopes, value, onChange }) {
    if (!scopes || scopes.length < 2) return null;
    return (
        <select value={value} onChange={e => onChange(e.target.value)} aria-label="Whose numbers"
            className="h-8 rounded-md border border-gray-300 bg-white px-2 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200">
            {scopes.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
    );
}

export function useSalesAgentAccess() {
    const { hasPermission, hasFeature } = useContext(AuthContext);
    const isSuperAdmin = sessionStorage.getItem("login_role") === "super_admin";
    const feature = hasFeature("sales_manager_agent");
    return {
        enabled: feature && (isSuperAdmin || hasPermission("sales_agent", "view")),
        canAct: isSuperAdmin || hasPermission("sales_agent", "edit"),
        canSettings: feature && (isSuperAdmin || hasPermission("sales_agent_settings", "view")),
        canEditSettings: isSuperAdmin || hasPermission("sales_agent_settings", "edit"),
    };
}

export const openLeadState = (id) => ({ state: { formEdit: true, row: { id } } });

export function EmptyState({ icon: Icon, title, children }) {
    return (
        <div className="flex flex-col items-center justify-center gap-1 py-8 text-center">
            {Icon && <Icon className="h-6 w-6 text-gray-300 dark:text-gray-600" />}
            <p className="text-sm font-medium text-gray-600 dark:text-gray-300">{title}</p>
            {children && <p className="max-w-md text-xs text-gray-400">{children}</p>}
        </div>
    );
}
